/**
 * Why an edit to a saved draft was refused (FIN-829). Each kind needs a different UI:
 * - `no_longer_editable`: it was marked sent meanwhile — refresh and show it as Sent (EC1).
 * - `changed_elsewhere`: someone saved it first — keep the user's edits, offer Reload (EC3).
 * - `gone`: it was deleted — say so and refresh the list (EC2).
 * Both apps' providers translate their own transport's error into this one type, so the
 * editor never branches on an HTTP status or a Connect code.
 */
export type DraftEditErrorKind = "no_longer_editable" | "changed_elsewhere" | "gone";

/** The saved draft an editor has open (FIN-829 F3) — its id, number and the version it loaded. */
export interface EditingDraft { id: string; number: string; version: number }

/** The heading each refusal is shown under, in both apps. */
export const DRAFT_EDIT_ERROR_TITLES: Record<DraftEditErrorKind, string> = {
  no_longer_editable: "This invoice has already been marked as sent",
  changed_elsewhere: "This draft was changed by someone else",
  gone: "This draft no longer exists",
};

/** The one wording per kind, so both apps say the same thing. */
export const DRAFT_EDIT_ERROR_MESSAGES: Record<DraftEditErrorKind, string> = {
  no_longer_editable: "This invoice has already been marked as sent and can no longer be edited.",
  changed_elsewhere: "This draft was changed by someone else. Reload to see the latest version.",
  gone: "This draft no longer exists. Someone may have deleted it.",
};

export class DraftEditError extends Error {
  readonly kind: DraftEditErrorKind;

  constructor(kind: DraftEditErrorKind, message: string = DRAFT_EDIT_ERROR_MESSAGES[kind]) {
    super(message);
    this.name = "DraftEditError";
    this.kind = kind;
  }
}

export function isDraftEditError(err: unknown): err is DraftEditError {
  return err instanceof DraftEditError;
}
